import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import Layout from '../../../components/Layout';
import api from '../../../api/axios';
import useAuthStore from '../../../store/authStore';
import { fmtDate, todayISO } from '../../../utils/dateFormat';
import SearchSelect from '../../../components/SearchSelect';
import { confirm } from '../../../components/ConfirmDialog';
import { canFiscalRelay } from '../../../utils/printService';
import { posCardCharge } from '../../../utils/tapxCharge';

const fmt = (v) => Number(v || 0).toFixed(2);

// ─── HOUSES TAB ──────────────────────────────────────────────────────────────
function HousesTab({ isAdmin }) {
    const [houses, setHouses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editHouse, setEditHouse] = useState(null);
    const [form, setForm] = useState({ name: '', sort_order: 0 });
    const [saving, setSaving] = useState(false);

    // Qizil Farel evləri (inteqrasiya aktivdirsə)
    const [qfEnabled, setQfEnabled] = useState(false);
    const [qfHouses, setQfHouses]   = useState([]);
    const [qfLoading, setQfLoading] = useState(false);

    const loadQfHouses = async () => {
        setQfLoading(true);
        try {
            const { data } = await api.get('/qizilfarel/houses');
            // sira_no-ya görə sırala (yoxdursa ada görə) — Eco-1,2,3 → Deluxe → Super-1,2 ardıcıl
            const sorted = (data?.data || []).sort((a, b) => {
                const sa = Number(a.sira_no ?? 9999), sb = Number(b.sira_no ?? 9999);
                if (sa !== sb) return sa - sb;
                return String(a.name).localeCompare(String(b.name), 'az', { numeric: true });
            });
            setQfHouses(sorted);
        } catch { /* ignore */ }
        finally { setQfLoading(false); }
    };

    const freeHouse = async (h) => {
        if (!(await confirm(`"${h.name}" evini boşaltmaq istəyirsiniz?`))) return;
        try {
            await api.post('/qizilfarel/houses/free', { house_name: h.name });
            toast.success(`${h.name} boşaldıldı.`);
            loadQfHouses();
        } catch (err) { toast.error(err.response?.data?.msg || 'Xəta baş verdi.'); }
    };

    const load = async () => {
        try {
            setLoading(true);
            const res = await api.get('/rental/houses');
            setHouses(res.data.data || []);
            // Qizil Farel statusu
            const { data: st } = await api.get('/qizilfarel/status').catch(() => ({ data: {} }));
            const enabled = Boolean(st?.enabled && st?.has_key);
            setQfEnabled(enabled);
            if (enabled) await loadQfHouses();
        } catch { toast.error('Evlər yüklənmədi.'); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const openAdd = () => { setEditHouse(null); setForm({ name: '', sort_order: 0 }); setShowForm(true); };
    const openEdit = (h) => { setEditHouse(h); setForm({ name: h.name, sort_order: h.sort_order }); setShowForm(true); };

    const save = async () => {
        if (!form.name.trim()) return toast.error('Ev adı daxil edin.');
        setSaving(true);
        try {
            if (editHouse) await api.put(`/rental/houses/${editHouse.id}`, form);
            else await api.post('/rental/houses', form);
            toast.success(editHouse ? 'Ev yeniləndi.' : 'Ev əlavə edildi.');
            setShowForm(false); load();
        } catch (err) { toast.error(err.response?.data?.msg || 'Xəta baş verdi.'); }
        finally { setSaving(false); }
    };

    const deleteHouse = async (h) => {
        if (!(await confirm(`"${h.name}" silinsin?`))) return;
        try { await api.delete(`/rental/houses/${h.id}`); toast.success('Ev silindi.'); load(); }
        catch (err) { toast.error(err.response?.data?.msg || 'Xəta baş verdi.'); }
    };

    // Qizil Farel aktivdirsə — onun evlərini göstər (dolu/boş + boşalt)
    if (qfEnabled) {
        const dolu = qfHouses.filter(h => h.occupied).length;
        return (
            <div>
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                    <p className="text-sm text-slate-500">
                        🏡 Qizil Farel evləri — <strong>{qfHouses.length}</strong> ev
                        <span className="ml-2 text-red-600 font-semibold">{dolu} dolu</span>
                        <span className="ml-2 text-green-600 font-semibold">{qfHouses.length - dolu} boş</span>
                    </p>
                    <button onClick={loadQfHouses} disabled={qfLoading}
                        className="px-3 py-1.5 rounded-lg text-xs font-bold bg-slate-100 text-slate-600 hover:bg-slate-200 disabled:opacity-50">
                        {qfLoading ? '...' : '🔄 Yenilə'}
                    </button>
                </div>

                {qfLoading ? <p className="text-center text-slate-400 py-10">Yüklənir...</p> : (
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {qfHouses.map(h => (
                            <div key={h.id} className={`rounded-2xl border-2 p-4 ${
                                h.occupied ? 'border-red-200 bg-red-50/40' : 'border-green-200 bg-green-50/40'
                            }`}>
                                <div className="flex items-center justify-between gap-2 mb-2">
                                    <span className="font-bold text-slate-800">{h.name}</span>
                                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${
                                        h.occupied ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                                    }`}>
                                        {h.occupied ? '🔴 Dolu' : '🟢 Boş'}
                                    </span>
                                </div>
                                {h.occupied ? (
                                    <>
                                        <p className="text-xs text-slate-500">🧑 {h.guest_name || '—'}</p>
                                        {h.occupied_at && <p className="text-[10px] text-slate-400 mt-0.5">{h.occupied_at}</p>}
                                        <button onClick={() => freeHouse(h)}
                                            className="mt-3 w-full py-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 text-xs font-bold border border-red-200">
                                            🚪 Evi boşalt
                                        </button>
                                    </>
                                ) : (
                                    <p className="text-xs text-slate-400 mt-1">Qonaqlar bölməsindən qəbul edin</p>
                                )}
                            </div>
                        ))}
                        {qfHouses.length === 0 && (
                            <p className="col-span-full text-center text-slate-400 py-10">Qizil Farel-də ev tapılmadı.</p>
                        )}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <p className="text-sm text-slate-500">{houses.length} ev qeydiyyatda</p>
                {isAdmin && (
                    <button onClick={openAdd}
                        className="px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700">
                        + Ev əlavə et
                    </button>
                )}
            </div>

            {loading ? <p className="text-center text-slate-400 py-10">Yüklənir...</p> : (
                <div className="grid gap-3">
                    {houses.map(h => (
                        <div key={h.id} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex items-center gap-4">
                            <div className="flex-1">
                                <div className="flex items-center gap-2">
                                    <span className="font-bold text-slate-800">{h.name}</span>
                                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${h.status === 'dolu' ? 'bg-red-100 text-red-700 border border-red-200' : 'bg-green-100 text-green-700 border border-green-200'}`}>
                                        {h.status === 'dolu' ? 'Dolu' : 'Boş'}
                                    </span>
                                </div>
                                {h.guest_name && (
                                    <p className="text-sm text-slate-500 mt-1">
                                        🧑 {h.guest_name} · {h.nights} gecə ·
                                        Cəmi: <span className="font-semibold">{fmt(h.total)} ₼</span> ·
                                        Qalıq: <span className="font-semibold text-red-600">{fmt(h.remaining)} ₼</span>
                                    </p>
                                )}
                            </div>
                            {isAdmin && (
                                <div className="flex gap-2">
                                    <button onClick={() => openEdit(h)}
                                        className="text-xs px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium">
                                        Düzəlt
                                    </button>
                                    {h.status === 'bos' && (
                                        <button onClick={() => deleteHouse(h)}
                                            className="text-xs px-3 py-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 font-medium">
                                            Sil
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                    {!houses.length && <p className="text-center text-slate-400 py-10">Hələ ev yoxdur.</p>}
                </div>
            )}

            {showForm && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
                        <h2 className="font-bold text-slate-800 mb-4">{editHouse ? 'Evi düzəlt' : 'Yeni ev'}</h2>
                        <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                            placeholder="Ev adı / nömrəsi"
                            className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm mb-3 outline-none focus:border-blue-400" />
                        <input type="number" value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: e.target.value }))}
                            placeholder="Sıra nömrəsi"
                            className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm mb-4 outline-none focus:border-blue-400" />
                        <div className="flex gap-2">
                            <button onClick={() => setShowForm(false)}
                                className="flex-1 py-2 rounded-xl border border-slate-200 text-sm text-slate-600">Ləğv et</button>
                            <button onClick={save} disabled={saving}
                                className="flex-1 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold disabled:opacity-50">
                                {saving ? 'Saxlanır...' : 'Saxla'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── GUESTS TAB ──────────────────────────────────────────────────────────────
function GuestsTab({ isAdmin }) {
    const [houses, setHouses] = useState([]);
    const [guests, setGuests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [subTab, setSubTab] = useState('aktiv');
    const [showCheckin, setShowCheckin] = useState(false);
    const [checkinForm, setCheckinForm] = useState({ house_id: '', guest_name: '', nights: 1, adults: 1, children: 0, note: '', total: '', deposit: '', check_in: todayISO() });
    const [saving, setSaving] = useState(false);
    const [payGuest, setPayGuest] = useState(null);
    const [payAmount, setPayAmount] = useState('');
    const [payMethod, setPayMethod] = useState('nagd');
    const [paying, setPaying] = useState(false);

    // Qizil Farel inteqrasiyası
    const [qfEnabled, setQfEnabled] = useState(false);
    const [qfReservations, setQfReservations] = useState([]);
    const [qfLoading, setQfLoading] = useState(false);
    const [qfMarkingId, setQfMarkingId] = useState(null);
    const [qfError, setQfError] = useState(null);
    const [qfSearch, setQfSearch] = useState('');

    const qfFiltered = qfReservations.filter(r => {
        if (!qfSearch.trim()) return true;
        const q = qfSearch.toLowerCase().trim();
        return (
            String(r.qonaq_adi || '').toLowerCase().includes(q) ||
            String(r.telefon   || '').toLowerCase().includes(q) ||
            String(r.ev_adi    || '').toLowerCase().includes(q)
        );
    });

    const loadQfStatus = async () => {
        try {
            const { data } = await api.get('/qizilfarel/status');
            setQfEnabled(Boolean(data?.enabled && data?.has_key));
        } catch { setQfEnabled(false); }
    };

    const loadQfReservations = async () => {
        setQfLoading(true);
        setQfError(null);
        try {
            const { data } = await api.get('/qizilfarel/reservations');
            setQfReservations(data?.data || []);
        } catch (err) {
            setQfError(err.response?.data?.msg || 'Yüklənmədi.');
            setQfReservations([]);
        } finally { setQfLoading(false); }
    };

    // Qonaq qəbul → evi dolu flag et
    const acceptGuest = async (r) => {
        if (!r.ev_adi) return toast.error('Ev adı yoxdur.');
        try {
            await api.post('/qizilfarel/houses/occupy', {
                house_name: r.ev_adi,
                reservation_id: r.id,
                guest_name: r.qonaq_adi,
            });
            toast.success(`${r.qonaq_adi} qəbul edildi — ${r.ev_adi} doludur.`);
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xəta baş verdi.');
        }
    };

    // Qizil Farel payment modal state
    const [qfPayRes, setQfPayRes] = useState(null);
    const [qfPayAmount, setQfPayAmount] = useState('');
    const [qfPayMethod, setQfPayMethod] = useState('nagd');
    const [qfCashAmt, setQfCashAmt] = useState('');
    const [qfCardAmt, setQfCardAmt] = useState('');
    const [qfPaying, setQfPaying] = useState(false);
    // POS kart çəkmə (evlər/qonaqlar ödənişində kart hissəsi terminala getsin)
    const [posTerminal, setPosTerminal] = useState(false);
    const [posPay, setPosPay] = useState({ open: false, message: '', closable: false, error: false });
    useEffect(() => {
        api.get('/orders/pos-terminal').then((r) => setPosTerminal(!!r.data?.pos_terminal)).catch(() => {});
    }, []);

    // Kart hissəsini terminalda çək (SmartOne relay + popup). true = davam et, false = imtina/xəta (dayan).
    // POS terminal yoxdursa çəkmə lazım deyil → true (adi qeyd).
    const chargeCard = async (cardPortion, label) => {
        if (!(Number(cardPortion) > 0) || !posTerminal || !canFiscalRelay()) return true;
        const amt = Number(cardPortion);
        setPosPay({ open: true, closable: false, error: false, message: `${amt.toFixed(2)} ₼ POS terminala göndərilir...` });
        try {
            // SmartOne 1 addım / Caspos TapXPhone 2 addım — posCardCharge idarə edir
            await posCardCharge(amt, label, (m) => setPosPay({ open: true, closable: false, error: false, message: m }));
            setPosPay({ open: true, closable: false, error: false, message: 'Ödəniş qəbul edildi. Yadda saxlanılır...' });
            return true;
        } catch (e) {
            setPosPay({ open: true, closable: true, error: true, message: e.response?.data?.msg || e.message || 'POS çəkiliş baş tutmadı / imtina.' });
            return false;
        }
    };

    const openQfPay = (r) => {
        const qaliq = Number(r.qaliq_mebleg || 0);
        setQfPayRes(r);
        setQfPayAmount(qaliq.toFixed(2));
        setQfPayMethod('nagd');
        setQfCashAmt('');
        setQfCardAmt('');
    };

    // Bölünmüş ödəniş: bir tərəfi yazanda o biri avtomatik «qalan» olur
    // (məs. cəmi 100, nağd 20 → kart 80). Heç bir tərəf cəmdən çox ola bilməz.
    const restOf = (total, part) => {
        const r = Math.round((Number(total || 0) - Number(part || 0)) * 100) / 100;
        return r > 0 ? r.toFixed(2) : '0.00';
    };
    const onCashChange = (v) => {
        const total = Number(qfPayAmount || 0);
        const cash = Number(v || 0) > total ? String(total) : v;
        setQfCashAmt(cash);
        setQfCardAmt(v === '' ? '' : restOf(total, cash));
    };
    const onCardChange = (v) => {
        const total = Number(qfPayAmount || 0);
        const card = Number(v || 0) > total ? String(total) : v;
        setQfCardAmt(card);
        setQfCashAmt(v === '' ? '' : restOf(total, card));
    };
    const onAmountChange = (v) => {
        setQfPayAmount(v);
        // Məbləğ dəyişəndə nağd qalır, kart yenidən hesablanır
        if (qfPayMethod === 'bolunmus' && qfCashAmt !== '') {
            const total = Number(v || 0);
            const cash = Math.min(Number(qfCashAmt || 0), total);
            setQfCashAmt(String(cash));
            setQfCardAmt(restOf(total, cash));
        }
    };

    const submitQfPay = async () => {
        if (!qfPayRes) return;
        const amount = Number(qfPayAmount);
        if (!amount || amount <= 0) return toast.error('Məbləğ daxil edin.');

        const body = { amount, method: qfPayMethod };
        if (qfPayMethod === 'bolunmus') {
            body.cash_amount = Number(qfCashAmt || 0);
            body.card_amount = Number(qfCardAmt || 0);
            if (Math.abs(body.cash_amount + body.card_amount - amount) > 0.01) {
                return toast.error('Nağd + kart cəmi ümumi məbləğə bərabər olmalıdır.');
            }
        }

        // Kart hissəsi: kart → tam məbləğ; bölünmüş → kart hissəsi
        const cardPortion = qfPayMethod === 'kart' ? amount : (qfPayMethod === 'bolunmus' ? Number(qfCardAmt || 0) : 0);
        setQfPaying(true);
        try {
            const charged = await chargeCard(cardPortion, `Ev: ${qfPayRes.ev_adi || qfPayRes.qonaq_adi || 'ödəniş'}`);
            if (!charged) { setQfPaying(false); return; } // imtina → qeyd etmə
            const { data } = await api.post(`/qizilfarel/reservations/${qfPayRes.id}/pay`, body);
            setPosPay({ open: false, message: '', closable: false, error: false });
            toast.success(data?.qf_updated
                ? 'Ödəniş alındı, Qizil Farel-də tam ödənilmiş kimi qeyd edildi ✓'
                : `Ödəniş alındı. Qalıq: ${Number(data?.remaining || 0).toFixed(2)} ₼`
            );
            setQfPayRes(null);
            await loadQfReservations();
        } catch (err) {
            setPosPay({ open: false, message: '', closable: false, error: false });
            toast.error(err.response?.data?.msg || 'Xəta baş verdi.');
        } finally { setQfPaying(false); }
    };

    useEffect(() => { loadQfStatus(); }, []);
    useEffect(() => { if (qfEnabled) loadQfReservations(); }, [qfEnabled]);

    const load = async () => {
        setLoading(true);
        try {
            const [hRes, gRes] = await Promise.all([
                api.get('/rental/houses'),
                api.get(`/rental/guests?status=${subTab}`)
            ]);
            setHouses(hRes.data.data || []);
            setGuests(gRes.data.data || []);
        } catch { toast.error('Məlumatlar yüklənmədi.'); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, [subTab]);

    const checkin = async () => {
        if (!checkinForm.house_id || !checkinForm.guest_name.trim() || !checkinForm.total)
            return toast.error('Ev, qonaq adı və cəmi məbləğ tələb olunur.');
        setSaving(true);
        try {
            await api.post('/rental/guests', checkinForm);
            toast.success('Qonaq qeyd edildi.');
            setShowCheckin(false);
            setCheckinForm({ house_id: '', guest_name: '', nights: 1, adults: 1, children: 0, note: '', total: '', deposit: '', check_in: todayISO() });
            load();
        } catch (err) { toast.error(err.response?.data?.msg || 'Xəta baş verdi.'); }
        finally { setSaving(false); }
    };

    const vacate = async (g) => {
        if (!(await confirm(`"${g.guest_name}" çıxsın və ev boşalsın?`))) return;
        try { await api.post(`/rental/guests/${g.id}/vacate`); toast.success('Ev boşaldıldı.'); load(); }
        catch (err) { toast.error(err.response?.data?.msg || 'Xəta baş verdi.'); }
    };

    const pay = async () => {
        if (!payAmount || Number(payAmount) <= 0) return toast.error('Məbləğ daxil edin.');
        const cardPortion = payMethod === 'kart' ? Number(payAmount) : 0;
        setPaying(true);
        try {
            const charged = await chargeCard(cardPortion, `Qonaq: ${payGuest?.guest_name || 'ödəniş'}`);
            if (!charged) { setPaying(false); return; }
            await api.post(`/rental/guests/${payGuest.id}/pay`, { amount: payAmount, method: payMethod });
            setPosPay({ open: false, message: '', closable: false, error: false });
            toast.success('Ödəniş qeyd edildi.');
            setPayGuest(null); setPayAmount(''); load();
        } catch (err) {
            setPosPay({ open: false, message: '', closable: false, error: false });
            toast.error(err.response?.data?.msg || 'Xəta baş verdi.');
        } finally { setPaying(false); }
    };

    const emptyHouses = houses.filter(h => h.status === 'bos');

    return (
        <div>
            {/* POS kart ödənişi prosesi — bloklayan popup (evlər/qonaqlar) */}
            {posPay.open && (
                <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4"
                     onClick={(e) => { if (posPay.closable && e.target === e.currentTarget) setPosPay({ open: false, message: '', closable: false, error: false }); }}>
                    <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl text-center">
                        <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center text-3xl mb-4 ${posPay.error ? 'bg-red-100' : 'bg-blue-100'}`}>
                            {posPay.error ? '❌' : <span className="inline-block h-8 w-8 rounded-full border-4 border-blue-200 border-t-blue-600 animate-spin" />}
                        </div>
                        <h3 className="text-base font-bold text-gray-800 mb-1">💳 POS ödənişi</h3>
                        <p className="text-sm text-gray-600 whitespace-pre-line leading-relaxed">{posPay.message}</p>
                        {posPay.closable ? (
                            <button onClick={() => setPosPay({ open: false, message: '', closable: false, error: false })}
                                className="mt-5 w-full py-2.5 rounded-xl text-sm font-bold text-white bg-gray-600 hover:bg-gray-700">Bağla</button>
                        ) : (
                            <p className="mt-4 text-[11px] text-gray-400">Zəhmət olmasa gözləyin — prosesi dayandırmayın.</p>
                        )}
                    </div>
                </div>
            )}
            <div className="flex items-center justify-between mb-4">
                <div className="flex gap-2">
                    {['aktiv', 'cixib'].map(t => (
                        <button key={t} onClick={() => setSubTab(t)}
                            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${subTab === t ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                            {t === 'aktiv' ? 'Aktiv' : 'Çıxmış'}
                        </button>
                    ))}
                </div>
                {subTab === 'aktiv' && !qfEnabled && (
                    <button onClick={() => setShowCheckin(true)}
                        className="px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700">
                        + Qonaq qəbul et
                    </button>
                )}
            </div>

            {/* ── Qizil Farel inteqrasiyası — aktiv rezervasiyalar ─────── */}
            {qfEnabled && (
                <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50/50 p-4">
                    <div className="flex items-center justify-between gap-2 mb-3">
                        <div className="flex items-center gap-2">
                            <span className="text-lg">🏡</span>
                            <h2 className="text-sm font-bold text-amber-900">Qizil Farel — aktiv rezervasiyalar</h2>
                            {!qfLoading && (
                                <span className="text-[11px] bg-amber-100 text-amber-700 rounded-full px-2 py-0.5 font-semibold">
                                    {qfSearch ? `${qfFiltered.length}/${qfReservations.length}` : qfReservations.length}
                                </span>
                            )}
                        </div>
                        <button
                            onClick={loadQfReservations}
                            disabled={qfLoading}
                            className="text-[11px] text-amber-700 hover:text-amber-900 rounded-lg px-2 py-1 border border-amber-200 disabled:opacity-50"
                        >
                            {qfLoading ? '...' : '🔄 Yenilə'}
                        </button>
                    </div>

                    {/* Axtarış */}
                    {!qfLoading && !qfError && qfReservations.length > 0 && (
                        <div className="relative mb-3">
                            <input
                                type="text"
                                value={qfSearch}
                                onChange={e => setQfSearch(e.target.value)}
                                placeholder="🔍 Qonaq adı, telefon və ya ev üzrə axtar..."
                                className="w-full rounded-xl border border-amber-200 bg-white px-3 py-2 pr-9 text-sm focus:outline-none focus:border-amber-400"
                            />
                            {qfSearch && (
                                <button
                                    type="button"
                                    onClick={() => setQfSearch('')}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 text-xs"
                                    aria-label="Təmizlə"
                                >
                                    ✕
                                </button>
                            )}
                        </div>
                    )}

                    {qfError ? (
                        <p className="text-sm text-red-600">⚠️ {qfError}</p>
                    ) : qfLoading ? (
                        <p className="text-sm text-amber-700/70">Yüklənir...</p>
                    ) : qfReservations.length === 0 ? (
                        <p className="text-sm text-amber-700/70">Aktiv ödənilməmiş rezervasiya yoxdur.</p>
                    ) : qfFiltered.length === 0 ? (
                        <p className="text-sm text-amber-700/70">"{qfSearch}" üzrə nəticə yoxdur.</p>
                    ) : (
                        <div className="grid gap-2">
                            {qfFiltered.map(r => {
                                const qaliq = Number(r.qaliq_mebleg || 0);
                                return (
                                    <div key={r.id} className="rounded-xl border border-amber-200 bg-white p-3">
                                        <div className="flex flex-wrap items-start justify-between gap-2">
                                            <div className="min-w-0 flex-1">
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <span className="font-bold text-slate-800">{r.qonaq_adi}</span>
                                                    <span className="text-[11px] bg-amber-100 text-amber-700 rounded-full px-2 py-0.5 font-semibold">{r.ev_adi}</span>
                                                    <span className="text-[11px] text-slate-400">📞 {r.telefon}</span>
                                                </div>
                                                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-2 text-xs">
                                                    <Stat label="Giriş" value={r.rezerv_tarixi} />
                                                    <Stat label="Gecə" value={r.gece_sayi} />
                                                    <Stat label="Böyük" value={r.boyuk_sayi} />
                                                    <Stat label="Uşaq" value={r.usaq_sayi || 0} />
                                                    <Stat
                                                        label="Qalıq"
                                                        value={`${fmt(qaliq)} ₼`}
                                                        cls={qaliq > 0 ? 'text-red-600 font-bold' : 'text-green-600 font-bold'}
                                                    />
                                                </div>
                                                <div className="mt-1.5 grid grid-cols-2 sm:grid-cols-3 gap-2 text-[11px] text-slate-500">
                                                    <span>Cəmi: <strong>{fmt(r.umumi_mebleg)} ₼</strong></span>
                                                    <span>Beh: <strong>{fmt(r.beh_meblegi)} ₼</strong></span>
                                                    <span>Ödənib: <strong>{fmt(r.odenilib_mebleg)} ₼</strong></span>
                                                </div>
                                                {r.qeyd && <p className="text-[11px] text-slate-400 mt-1.5">📝 {r.qeyd}</p>}
                                            </div>
                                            <div className="flex flex-col gap-1.5 shrink-0">
                                                <button
                                                    onClick={() => acceptGuest(r)}
                                                    className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold whitespace-nowrap"
                                                >
                                                    🔑 Qəbul et
                                                </button>
                                                <button
                                                    onClick={() => openQfPay(r)}
                                                    className="px-3 py-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-xs font-bold whitespace-nowrap"
                                                >
                                                    💳 Ödəniş
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {loading ? <p className="text-center text-slate-400 py-10">Yüklənir...</p> : (
                <div className="grid gap-3">
                    {guests.map(g => (
                        <div key={g.id} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-bold text-slate-800">{g.guest_name}</span>
                                        <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">{g.house_name}</span>
                                    </div>
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 text-sm">
                                        <Stat label="Giriş" value={fmtDate(g.check_in)} />
                                        <Stat label="Gecə"  value={g.nights} />
                                        <Stat label="Böyük" value={g.adults} />
                                        <Stat label="Uşaq"  value={g.children} />
                                    </div>
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2 text-sm">
                                        <Stat label="Cəmi" value={`${fmt(g.total)} ₼`} cls="text-slate-800 font-bold" />
                                        <Stat label="Beh" value={`${fmt(g.deposit)} ₼`} cls="text-blue-600 font-bold" />
                                        <Stat label="Ödəndi" value={`${fmt(g.paid)} ₼`} cls="text-green-600 font-bold" />
                                        <Stat label="Qalıq" value={`${fmt(g.remaining)} ₼`} cls={Number(g.remaining) > 0 ? 'text-red-600 font-bold' : 'text-green-600 font-bold'} />
                                    </div>
                                    {g.note && <p className="text-xs text-slate-400 mt-2">📝 {g.note}</p>}
                                </div>
                                {subTab === 'aktiv' && (
                                    <div className="flex flex-col gap-2 shrink-0">
                                        <button onClick={() => { setPayGuest(g); setPayAmount(''); }}
                                            className="text-xs px-3 py-1.5 rounded-lg bg-green-50 hover:bg-green-100 text-green-700 font-semibold whitespace-nowrap">
                                            💳 Ödəniş
                                        </button>
                                        {(() => {
                                            const hasDebt = Number(g.remaining) > 0;
                                            const blocked = hasDebt && !isAdmin;
                                            return (
                                                <button
                                                    onClick={() => blocked
                                                        ? toast.error(`Qalıq: ${fmt(g.remaining)} ₼. Ödənilmədən boşaldıla bilməz.`)
                                                        : vacate(g)
                                                    }
                                                    className={`text-xs px-3 py-1.5 rounded-lg font-semibold whitespace-nowrap transition-all ${
                                                        blocked
                                                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                            : 'bg-red-50 hover:bg-red-100 text-red-600'
                                                    }`}
                                                    title={blocked ? `Qalıq ${fmt(g.remaining)} ₼ — yalnız admin boşalda bilər` : 'Evi boşalt'}
                                                >
                                                    🚪 Evi boşalt{blocked && ' 🔒'}
                                                </button>
                                            );
                                        })()}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                    {!guests.length && (
                        <p className="text-center text-slate-400 py-10">
                            {subTab === 'aktiv' ? 'Aktiv qonaq yoxdur.' : 'Çıxmış qonaq yoxdur.'}
                        </p>
                    )}
                </div>
            )}

            {/* Check-in Modal */}
            {showCheckin && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4 overflow-y-auto">
                    <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl my-4">
                        <h2 className="font-bold text-slate-800 mb-4 text-lg">Qonaq qəbul et</h2>
                        <div className="grid gap-3">
                            <FSelect label="Ev" value={checkinForm.house_id}
                                onChange={v => setCheckinForm(f => ({ ...f, house_id: v }))}
                                options={emptyHouses.map(h => ({ value: h.id, label: h.name }))}
                                placeholder="Boş ev seçin" />
                            <FInput label="Qonaq adı" value={checkinForm.guest_name}
                                onChange={v => setCheckinForm(f => ({ ...f, guest_name: v }))} />
                            <div className="grid grid-cols-3 gap-2">
                                <FNum label="Gecə" value={checkinForm.nights} onChange={v => setCheckinForm(f => ({ ...f, nights: v }))} />
                                <FNum label="Böyük" value={checkinForm.adults} onChange={v => setCheckinForm(f => ({ ...f, adults: v }))} />
                                <FNum label="Uşaq" value={checkinForm.children} onChange={v => setCheckinForm(f => ({ ...f, children: v }))} />
                            </div>
                            <FInput label="Giriş tarixi" type="date" value={checkinForm.check_in}
                                onChange={v => setCheckinForm(f => ({ ...f, check_in: v }))} />
                            <div className="grid grid-cols-2 gap-2">
                                <FNum label="Cəmi (₼)" value={checkinForm.total} onChange={v => setCheckinForm(f => ({ ...f, total: v }))} step="0.01" />
                                <FNum label="Beh (₼)" value={checkinForm.deposit} onChange={v => setCheckinForm(f => ({ ...f, deposit: v }))} step="0.01" />
                            </div>
                            <FInput label="Qeyd (ixtiyari)" value={checkinForm.note}
                                onChange={v => setCheckinForm(f => ({ ...f, note: v }))} />
                        </div>
                        <div className="flex gap-2 mt-4">
                            <button onClick={() => setShowCheckin(false)}
                                className="flex-1 py-2 rounded-xl border border-slate-200 text-sm text-slate-600">Ləğv et</button>
                            <button onClick={checkin} disabled={saving}
                                className="flex-1 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold disabled:opacity-50">
                                {saving ? 'Saxlanır...' : 'Qeyd et'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ─── Qizil Farel Payment Modal ────────────────────────────── */}
            {qfPayRes && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
                        <h2 className="font-bold text-slate-800 mb-1">💳 Ödəniş — {qfPayRes.qonaq_adi}</h2>
                        <div className="text-xs text-slate-500 mb-4 grid grid-cols-2 gap-1">
                            <span>🏡 {qfPayRes.ev_adi}</span>
                            <span>Cəmi: <strong>{fmt(qfPayRes.umumi_mebleg)} ₼</strong></span>
                            <span>Beh: <strong>{fmt(qfPayRes.beh_meblegi)} ₼</strong></span>
                            <span>Qalıq: <strong className="text-red-600">{fmt(qfPayRes.qaliq_mebleg)} ₼</strong></span>
                        </div>

                        <div className="grid gap-3">
                            <FNum label="Ödəniş məbləği (₼)" value={qfPayAmount}
                                onChange={onAmountChange} step="0.01" />

                            <div>
                                <label className="text-xs text-slate-500 mb-1 block">Ödəniş üsulu</label>
                                <div className="grid grid-cols-3 gap-2">
                                    {[
                                        { k: 'nagd',     l: '💵 Nağd'     },
                                        { k: 'kart',     l: '💳 Kart'     },
                                        { k: 'bolunmus', l: '🔀 Bölünmüş' },
                                    ].map(m => (
                                        <button key={m.k} type="button" onClick={() => setQfPayMethod(m.k)}
                                            className={`py-2 rounded-xl text-xs font-semibold transition-colors ${qfPayMethod === m.k ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'}`}>
                                            {m.l}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {qfPayMethod === 'bolunmus' && (
                                <div className="grid grid-cols-2 gap-2 p-3 rounded-xl bg-slate-50 border border-slate-200">
                                    <FNum label={`Nağd (₼) — max ${Number(qfPayAmount || 0).toFixed(2)}`}
                                        value={qfCashAmt} onChange={onCashChange} step="0.01" />
                                    <FNum label={`Kart (₼) — max ${Number(qfPayAmount || 0).toFixed(2)}`}
                                        value={qfCardAmt} onChange={onCardChange} step="0.01" />
                                    <p className="col-span-2 text-[11px] text-slate-500">
                                        Cəmi: <strong>{(Number(qfCashAmt || 0) + Number(qfCardAmt || 0)).toFixed(2)} ₼</strong>
                                        {' / '}
                                        Tələb: <strong>{Number(qfPayAmount || 0).toFixed(2)} ₼</strong>
                                    </p>
                                </div>
                            )}
                        </div>

                        <div className="flex gap-2 mt-4">
                            <button onClick={() => setQfPayRes(null)}
                                className="flex-1 py-2 rounded-xl border border-slate-200 text-sm text-slate-600">Ləğv et</button>
                            <button onClick={submitQfPay} disabled={qfPaying}
                                className="flex-1 py-2 rounded-xl bg-green-600 text-white text-sm font-semibold disabled:opacity-50">
                                {qfPaying ? 'Saxlanır...' : 'Ödənişi qəbul et'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Payment Modal */}
            {payGuest && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
                        <h2 className="font-bold text-slate-800 mb-1">Ödəniş — {payGuest.guest_name}</h2>
                        <p className="text-sm text-slate-500 mb-4">Qalıq: <span className="font-bold text-red-600">{fmt(payGuest.remaining)} ₼</span></p>
                        <div className="grid gap-3">
                            <FNum label="Məbləğ (₼)" value={payAmount} onChange={v => setPayAmount(v)} step="0.01" />
                            <div>
                                <label className="text-xs text-slate-500 mb-1 block">Ödəniş üsulu</label>
                                <div className="flex gap-2">
                                    {['nagd', 'kart'].map(m => (
                                        <button key={m} onClick={() => setPayMethod(m)}
                                            className={`flex-1 py-2 rounded-xl text-sm font-semibold transition-colors ${payMethod === m ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'}`}>
                                            {m === 'nagd' ? 'Nağd' : 'Kart'}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="flex gap-2 mt-4">
                            <button onClick={() => setPayGuest(null)}
                                className="flex-1 py-2 rounded-xl border border-slate-200 text-sm text-slate-600">Ləğv et</button>
                            <button onClick={pay} disabled={paying}
                                className="flex-1 py-2 rounded-xl bg-green-600 text-white text-sm font-semibold disabled:opacity-50">
                                {paying ? 'Saxlanır...' : 'Ödənişi qeyd et'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
export default function Rental() {
    const { user } = useAuthStore();
    const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
    const [searchParams] = useSearchParams();
    const [tab, setTab] = useState(searchParams.get('tab') || 'evler');

    useEffect(() => {
        const t = searchParams.get('tab');
        if (t) setTab(t);
    }, [searchParams]);

    const tabs = [
        { key: 'evler', label: '🏠 Evlər' },
        { key: 'qonaqlar', label: '🧑 Qonaqlar' },
    ];

    return (
        <Layout>
            <div className="p-4 max-w-4xl mx-auto">
                <h1 className="text-xl font-bold text-slate-800 mb-4">Ev Kirayəsi</h1>

                <div className="flex gap-2 mb-5">
                    {tabs.map(t => (
                        <button key={t.key} onClick={() => setTab(t.key)}
                            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${tab === t.key ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                            {t.label}
                        </button>
                    ))}
                </div>

                {tab === 'evler' && <HousesTab isAdmin={isAdmin} />}
                {tab === 'qonaqlar' && <GuestsTab isAdmin={isAdmin} />}
            </div>
        </Layout>
    );
}

// ─── HELPERS ─────────────────────────────────────────────────────────────────
const Stat = ({ label, value, cls = 'text-slate-700' }) => (
    <div>
        <p className="text-xs text-slate-400">{label}</p>
        <p className={`font-semibold ${cls}`}>{value}</p>
    </div>
);

const FInput = ({ label, value, onChange, type = 'text' }) => (
    <div>
        <label className="text-xs text-slate-500 mb-1 block">{label}</label>
        <input type={type} value={value} onChange={e => onChange(e.target.value)}
            className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-blue-400" />
    </div>
);

const FNum = ({ label, value, onChange, step = '1' }) => (
    <div>
        <label className="text-xs text-slate-500 mb-1 block">{label}</label>
        <input type="number" step={step} value={value} onChange={e => onChange(e.target.value)}
            className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-blue-400" />
    </div>
);

const FSelect = ({ label, value, onChange, options, placeholder }) => (
    <div>
        <label className="text-xs text-slate-500 mb-1 block">{label}</label>
        <SearchSelect value={value} onChange={onChange} options={options} placeholder={placeholder} />
    </div>
);
